import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Sparkles, Check, Wand2, Brain, Eye } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function AIGeneration({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=70%', pin: true, scrub: 0.6 },
      });
      scrollTl.fromTo(contentRef.current, { x: '-20vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0);
      scrollTl.fromTo(visualRef.current, { x: '30vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0.05);
      scrollTl.fromTo(contentRef.current, { x: 0, opacity: 1 }, { x: '-10vw', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(visualRef.current, { x: 0, opacity: 1 }, { x: '10vw', opacity: 0, ease: 'power2.in' }, 0.72);
    }, section);

    return () => ctx.revert();
  }, []);

  const features = [
    { icon: Brain, title: 'Context-Aware', description: 'AI knows which artists are performing and uses their actual logos.' },
    { icon: Eye, title: 'Brand Consistent', description: 'Uses your stored brand colors, fonts, and visual identity.' },
    { icon: Wand2, title: 'One-Click Generate', description: 'Select an event, click generate—flyer ready in seconds.' },
  ];

  return (
    <section ref={sectionRef} className={`pinned-section bg-[#0B0F10] flex items-center ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div ref={contentRef} style={{ willChange: 'transform, opacity' }}>
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#FF6B4A] mb-4 block">AI Generation</span>
            <h2 className="font-display font-bold text-[clamp(36px,4.5vw,64px)] leading-tight text-white mb-6">
              Grounded in<br />
              <span className="text-[#FF6B4A]">your assets.</span>
            </h2>
            <p className="text-lg text-[#A9B4B8] leading-relaxed mb-8">
              Unlike generic AI tools, Event.Tools generates flyers using your actual logos, headshots, and brand elements—never random images that miss the mark.
            </p>
            <div className="space-y-4">
              {features.map((feature, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF6B4A]/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon size={20} className="text-[#FF6B4A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-[#A9B4B8]">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div ref={visualRef} className="relative" style={{ willChange: 'transform, opacity' }}>
            <div className="bg-[#122A2F] rounded-xl p-6 border border-[#2EC4B6]/30 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-xs text-[#2EC4B6] font-mono uppercase">Next Event</span>
                  <h3 className="font-display font-semibold text-xl text-white">Midnight Sessions</h3>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#2EC4B6]/20 flex items-center justify-center">
                  <span className="text-[#2EC4B6] font-bold">28</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#A9B4B8] mb-4">
                <span>Warehouse 21</span>
                <span>•</span>
                <span>DJ Shadow, Aphex Twin, Massive Attack</span>
              </div>
              <button className="w-full py-3 bg-[#2EC4B6] text-[#0B0F10] font-semibold rounded-lg flex items-center justify-center gap-2">
                <Sparkles size={18} />
                Generate Flyer
              </button>
            </div>
            <div className="bg-[#1a1f21] rounded-xl p-4 border border-white/10">
              <div className="text-xs text-[#A9B4B8] mb-3 uppercase tracking-wider">AI Context</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1.5 bg-[#FF6B4A]/20 text-[#FF6B4A] text-sm rounded-full flex items-center gap-2">
                  <Check size={14} />
                  Golden Nugget logo
                </span>
                <span className="px-3 py-1.5 bg-[#2EC4B6]/20 text-[#2EC4B6] text-sm rounded-full flex items-center gap-2">
                  <Check size={14} />
                  DJ Shadow headshot
                </span>
                <span className="px-3 py-1.5 bg-[#2EC4B6]/20 text-[#2EC4B6] text-sm rounded-full flex items-center gap-2">
                  <Check size={14} />
                  Venue brand colors
                </span>
              </div>
            </div>
            <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B4A]/10 to-[#2EC4B6]/10 blur-2xl opacity-30 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
